import { Check, ArrowRight, ChevronDown } from "lucide-react";

export default function ProductSection({
  id,
  reverse = false, // true = text left, image right
  altBg = false,
  badgeType = "remote",
  badgeLabel,
  title,
  description,
  features = [],
  ctaLabel,
  imageSrc,
  imageAlt,
  imageCaption,
  variant = "simple", // "simple" | "extended"
  imageBleed = false, // used if variant="extended"
  accordions = [], // array of {title, content}
  preCtaNote,       // optional JSX/text shown before the CTA button
}) {
  const badgeClasses =
    badgeType === "clinic"
      ? "bg-nuuma-accent2 text-white"
      : badgeType === "remote-ivf"
      ? "bg-[#1e87f0] text-white"
      : "bg-nuuma-grey200 text-nuuma-primary";

  if (variant === "extended") {
    const textIsLeft = reverse; // If reverse=true, text is on the left (order-1 desktop)

    return (
      <section
        id={id}
        data-testid={`product-section-${id}`}
        className="relative py-20 lg:py-24 overflow-hidden"
      >
        <div className="nuuma-container">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
            
            {/* Content Container */}
            <div
              className={`relative flex flex-col justify-center py-8 ${
                textIsLeft ? "lg:pr-8 order-2 lg:order-1" : "lg:pl-8 order-2 lg:order-2"
              }`}
            >
              {/* Grey background that extends to the edge */}
              <div
                className={`absolute inset-y-0 ${
                  textIsLeft
                    ? "-left-[100vw] -right-6 lg:right-0 rounded-r-[40px] lg:rounded-r-[50px]"
                    : "-right-[100vw] -left-6 lg:left-0 rounded-l-[40px] lg:rounded-l-[50px]"
                } bg-nuuma-grey100 pointer-events-none`}
              />

              <div className="relative z-10 flex flex-col items-start">
                <span
                  data-testid={`product-badge-${id}`}
                  className={`inline-flex w-fit items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide ${badgeClasses}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                  {badgeLabel}
                </span>

                <h2
                  data-testid={`product-title-${id}`}
                  className="mt-4 font-display font-bold text-nuuma-primary text-3xl sm:text-4xl lg:text-[2.4rem] leading-[1.15]"
                >
                  {title}
                </h2>

                <p className="mt-3 text-[1rem] text-nuuma-grey700 leading-relaxed max-w-xl">
                  {description}
                </p>

                {accordions && accordions.length > 0 ? (
                  <div className="mt-5 w-full border-t border-nuuma-grey200">
                    {accordions.map((acc, i) => (
                      <details
                        key={i}
                        className="group border-b border-nuuma-grey200 transition-all duration-300"
                      >
                        <summary className="flex cursor-pointer items-center justify-between py-3.5 font-semibold text-nuuma-primary marker:content-none [&::-webkit-details-marker]:hidden hover:opacity-70 transition-opacity">
                          <span className="text-[1.05rem]">{acc.title}</span>
                          <ChevronDown className="h-5 w-5 text-nuuma-grey500 transition-transform duration-300 group-open:rotate-180" />
                        </summary>
                        <div className="pb-4 pt-1 pr-6 text-nuuma-grey700 text-[0.95rem] leading-relaxed">
                          {acc.content}
                        </div>
                      </details>
                    ))}
                  </div>
                ) : (
                  <ul className="mt-5 space-y-3.5">
                    {features.map((f, i) => (
                      <li
                        key={i}
                        className="flex gap-3 items-start text-nuuma-grey900"
                      >
                        <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-nuuma-success/10 flex items-center justify-center">
                          <Check className="h-3 w-3 text-nuuma-success" strokeWidth={3} />
                        </span>
                        <span className="text-[0.95rem] leading-relaxed">
                          <strong className="text-nuuma-primary">{f.label}:</strong>{" "}
                          {f.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {preCtaNote && (
                  <p className="mt-6 text-sm text-nuuma-grey600 leading-relaxed">{preCtaNote}</p>
                )}
                <a
                  href="#kontakt"
                  data-testid={`product-cta-${id}`}
                  className="btn-pill btn-primary-nuuma mt-4 w-fit !py-[8px]"
                >
                  {ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Image Container */}
            <div
              className={`relative flex items-start w-full ${
                textIsLeft ? "order-1 lg:order-2 justify-end" : "order-1 lg:order-1"
              }`}
            >
              <div
                data-testid={`${id}-image-wrapper`}
                className={
                  imageBleed
                    ? `absolute inset-y-0 ${
                        textIsLeft
                          ? "-right-[20px] left-0 lg:right-auto lg:w-[calc(50vw-2rem)] rounded-l-[40px] lg:rounded-l-[50px]"
                          : "-left-[20px] right-0 lg:left-auto lg:w-[calc(50vw-2rem)] rounded-r-[40px] lg:rounded-r-[50px]"
                      } overflow-hidden shadow-nuuma-m`
                    : `w-[96%] aspect-[4/5] lg:aspect-[7/10] sticky top-28 overflow-hidden rounded-[50px] shadow-nuuma-m`
                }
              >
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {imageCaption && (
                  <div className="absolute bottom-4 left-4 lg:left-8 bg-white/95 backdrop-blur-md rounded-full px-4 py-2 text-xs font-medium text-nuuma-primary shadow-nuuma-s">
                    {imageCaption}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    );
  }

  // Fallback to simple layout for backwards compatibility (Zdrowie section)
  return (
    <section
      id={id}
      data-testid={`product-section-${id}`}
      className={`py-20 lg:py-24 ${altBg ? "bg-nuuma-grey100" : "bg-white"}`}
    >
      <div className="nuuma-container">
        <div
          className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
            reverse ? "lg:[&>*:first-child]:order-2" : ""
          }`}
        >
          {/* Content */}
          <div>
            <span
              data-testid={`product-badge-${id}`}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide ${badgeClasses}`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
              {badgeLabel}
            </span>

            <h2
              data-testid={`product-title-${id}`}
              className="mt-5 font-display font-bold text-nuuma-primary text-3xl sm:text-4xl lg:text-[2.4rem] leading-[1.15]"
            >
              {title}
            </h2>

            <p className="mt-4 text-[1rem] text-nuuma-grey700 leading-relaxed max-w-xl">
              {description}
            </p>

            {accordions && accordions.length > 0 ? (
              <div className="mt-7 w-full border-t border-nuuma-grey200">
                {accordions.map((acc, i) => (
                  <details
                    key={i}
                    className="group border-b border-nuuma-grey200 transition-all duration-300"
                  >
                    <summary className="flex cursor-pointer items-center justify-between py-3.5 font-semibold text-nuuma-primary marker:content-none [&::-webkit-details-marker]:hidden hover:opacity-70 transition-opacity">
                      <span className="text-[1.05rem]">{acc.title}</span>
                      <ChevronDown className="h-5 w-5 text-nuuma-grey500 transition-transform duration-300 group-open:rotate-180" />
                    </summary>
                    <div className="pb-4 pt-1 pr-6 text-nuuma-grey700 text-[0.95rem] leading-relaxed">
                      {acc.content}
                    </div>
                  </details>
                ))}
              </div>
            ) : (
              <ul className="mt-7 space-y-3.5">
                {features.map((f, i) => (
                  <li
                    key={i}
                    className="flex gap-3 items-start text-nuuma-grey900"
                  >
                    <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-nuuma-success/10 flex items-center justify-center">
                      <Check className="h-3 w-3 text-nuuma-success" strokeWidth={3} />
                    </span>
                    <span className="text-[0.95rem] leading-relaxed">
                      {typeof f === "string" ? (
                        f
                      ) : (
                        <>
                          <strong className="text-nuuma-primary">
                            {f.label}:
                          </strong>{" "}
                          {f.text}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <a
              href="#kontakt"
              data-testid={`product-cta-${id}`}
              className="btn-pill btn-primary-nuuma mt-9"
            >
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative rounded-nuuma-l overflow-hidden shadow-nuuma-m bg-nuuma-grey100 aspect-[4/3]">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-nuuma-grey500 text-sm">
                  {imageCaption}
                </div>
              )}
              {/* Floating caption chip */}
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-md rounded-full px-4 py-2 text-xs font-medium text-nuuma-primary shadow-nuuma-s">
                {imageCaption}
              </div>
            </div>
            {/* Decorative accent */}
            <div
              aria-hidden
              className="absolute -z-10 -top-6 -right-6 h-32 w-32 rounded-full bg-nuuma-secondary/10"
            />
            <div
              aria-hidden
              className="absolute -z-10 -bottom-6 -left-6 h-28 w-28 rounded-full bg-nuuma-accent1/15"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
